import { useCallback, useRef, useState } from 'react'
import { localizeFoundStockError } from './foundStockErrors'

// The three settlement dialogs share one outcome contract:
//
//   request() resolves ──────────────────────────────► onSuccess(toMessage(result))
//   request() rejects ─► localizeFoundStockError(error)
//                          ├─ gateOff        ─► onGateOff()                     (no message set)
//                          ├─ refetch        ─► errorMessage = message, onStale(message)
//                          └─ otherwise      ─► errorMessage = message          (dialog stays open)
//                                               (+ errorCoverageEventId when the refusal names an event)
//
// `run` is a no-op while a request is already in flight.
export const useSettlementRequest = ({ onSuccess = () => {}, onGateOff = () => {}, onStale = () => {} }) => {
  const [busy, setBusy] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [errorCoverageEventId, setErrorCoverageEventId] = useState(null)
  const busyRef = useRef(false)

  const reset = useCallback(() => {
    busyRef.current = false
    setBusy(false)
    setErrorMessage(null)
    setErrorCoverageEventId(null)
  }, [])

  const run = useCallback(
    async (request, toMessage) => {
      if (busyRef.current) return { ok: false }
      busyRef.current = true
      setBusy(true)
      setErrorMessage(null)
      setErrorCoverageEventId(null)
      let result
      try {
        result = await request()
      } catch (error) {
        busyRef.current = false
        setBusy(false)
        const outcome = localizeFoundStockError(error)
        if (outcome.gateOff) {
          onGateOff()
          return { ok: false, outcome }
        }
        setErrorMessage(outcome.message)
        setErrorCoverageEventId(outcome.coverageEventId ?? null)
        if (outcome.refetch) onStale(outcome.message)
        return { ok: false, outcome }
      }
      busyRef.current = false
      setBusy(false)
      // Outside the try on purpose: only the request itself is guarded. A throw from toMessage or
      // from onSuccess must not report a settlement that DID happen (billed, emailed) as a failure.
      onSuccess(toMessage(result))
      return { ok: true, result }
    },
    [onSuccess, onGateOff, onStale],
  )

  return { busy, errorMessage, errorCoverageEventId, reset, run }
}
